import {AfterViewInit, ChangeDetectorRef, Directive, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {NgxSpinnerService} from 'ngx-spinner';
import {Observable, firstValueFrom} from 'rxjs';
import {ErrorHandlingComponent} from '../components/error-handling/error-handling.component';
import {IncidentsService} from '../services/incidents.service';
import {createInfiniteScrollObserver} from './infinite-scroll.util';

@Directive()
export abstract class PaginatedIncidentComponentBase<TIncident, TResponse extends { incidents?: TIncident[] }>
  implements OnInit, AfterViewInit, OnDestroy {
  protected readonly pageSize = 100;
  protected skip = 0;
  protected hasMoreIncidents = true;
  protected isLoadingIncidents = false;
  protected activeFilter?: string;
  protected intersectionObserver?: IntersectionObserver;

  @ViewChild('scrollSentinel') protected scrollSentinel?: ElementRef<HTMLDivElement>;

  protected constructor(
    protected readonly incidentService: IncidentsService,
    protected readonly spinnerService: NgxSpinnerService,
    protected readonly cdRef: ChangeDetectorRef,
    protected readonly modalService: NgbModal,
  ) {}

  protected get requiresAuthToken(): boolean {
    return false;
  }

  protected abstract get incidentPage(): { incidents: TIncident[] } | undefined;
  protected abstract set incidentPage(value: { incidents: TIncident[] } | undefined);
  protected abstract get filterValue(): string | undefined;
  protected abstract fetchPage(licencePlate?: string, skip?: number, limit?: number): Observable<TResponse>;

  /* istanbul ignore next */
  ngOnInit(): void {
    void this.loadIncidents(this.filterValue);
  }

  /* istanbul ignore next */
  ngAfterViewInit(): void {
    this.attachScrollListener();
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  /* istanbul ignore next */
  async getIncidents(licencePlate?: string): Promise<void> {
    await this.loadIncidents(licencePlate);
  }

  /* istanbul ignore next */
  openReport(incidentId: any): void {
    this.spinnerService.show();
    this.incidentService.openReport(incidentId).subscribe({
      next: () => {
        void this.spinnerService.hide();
      },
      error: () => {
        void this.spinnerService.hide();
      }
    });
  }

  protected async loadIncidents(licencePlate?: string): Promise<void> {
    if (this.requiresAuthToken && !this.hasAuthToken()) {
      console.info('No auth token found, waiting for user to authenticate...');
      return;
    }

    this.resetPagination(licencePlate);
    await this.loadNextPage();
  }

  protected resetPagination(licencePlate?: string): void {
    this.activeFilter = licencePlate;
    this.skip = 0;
    this.hasMoreIncidents = true;
    this.incidentPage = { incidents: [] } as { incidents: TIncident[] };
  }

  protected mergeIncidents(response: TResponse | undefined): void {
    const existingIncidents = this.incidentPage?.incidents ?? [];
    const nextIncidents = response?.incidents ?? [];
    this.incidentPage = {
      ...(response ?? {}),
      incidents: [...existingIncidents, ...nextIncidents],
    } as { incidents: TIncident[] };
  }

  protected async loadNextPage(): Promise<void> {
    if (this.isLoadingIncidents || !this.hasMoreIncidents) {
      return;
    }

    this.isLoadingIncidents = true;
    this.spinnerService.show();
    try {
      const response = await firstValueFrom(this.fetchPage(this.activeFilter, this.skip, this.pageSize));
      const loadedCount = response?.incidents?.length ?? 0;
      this.mergeIncidents(response);
      this.skip += loadedCount;
      this.hasMoreIncidents = loadedCount === this.pageSize;
      this.cdRef.detectChanges();
    } catch (error: any) {
      if (error?.status) {
        const modalRef = this.modalService.open(ErrorHandlingComponent, {
          centered: true,
          backdropClass: 'backdrop-class',
        });
        modalRef.componentInstance.error = error;
      }
    } finally {
      this.isLoadingIncidents = false;
      this.spinnerService.hide();
    }
  }

  protected attachScrollListener(): void {
    this.intersectionObserver = createInfiniteScrollObserver({
      target: this.scrollSentinel?.nativeElement,
      previousObserver: this.intersectionObserver,
      onIntersect: () => {
        if (this.hasMoreIncidents && !this.isLoadingIncidents) {
          void this.loadNextPage();
        }
      },
    });
  }

  private hasAuthToken(): boolean {
    const database = sessionStorage.getItem('database');
    return Boolean(sessionStorage.getItem('authToken_' + database));
  }
}
